import { config } from "@/config";
import type { UserDTO } from "@/models/dtos/user-dto";
import { Role } from "@/models/enums/role";
import {
  type AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";
import { ref } from "vue";
import { http } from "./http";

interface AuthenticationState {
  user?: UserDTO;
}

function getDefaultState(): AuthenticationState {
  return {
    user: undefined,
  };
}

class AuthService {
  private client: PublicClientApplication;
  private lastValidUserAccount: AccountInfo | undefined;
  private isFirstTokenRefresh = true;
  private readonly LOCAL_STORAGE_AUTH = "OCP:AUTH";
  private state = ref(getDefaultState());

  private clearState() {
    localStorage.removeItem(this.LOCAL_STORAGE_AUTH);
    this.state.value = getDefaultState();
  }

  constructor() {
    this.client = new PublicClientApplication({
      auth: {
        clientId: config.VITE_MS_CLIENT_ID,
        authority: config.VITE_MS_AUTHORITY,
        redirectUri: window.location.origin, // Use full origin instead of just "/"
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: "sessionStorage", // or "localStorage"
      },
      system: {
        loggerOptions: {
          piiLoggingEnabled: false,
        },
      },
    });
  }

  async init() {
    await this.client.initialize();

    // Add this to handle redirect promises on initial load
    try {
      await this.handleLoginRedirects();
    } catch (error) {
      console.error("Error handling redirect:", error);
    }
  }

  async handleLoginRedirects() {
    try {
      // Explicitly handle redirect promise
      const response = await this.client.handleRedirectPromise();

      // If there's a response, you might want to set the account or perform additional actions
      if (response) {
        const account = this.client.getAllAccounts()[0];
        if (account) {
          this.lastValidUserAccount = account;
        }
      }

      return !!response;
    } catch (error) {
      console.error("Error in handleLoginRedirects:", error);
      throw error;
    }
  }

  async login() {
    await this.init();

    // Try popup first
    try {
      await this.client.loginPopup();
    } catch (error) {
      // If popup fails, fall back to redirect
      console.warn("Popup login failed, falling back to redirect", error);
      await this.client.loginRedirect();
    }
  }

  async logout() {
    await this.init();
    this.lastValidUserAccount = undefined;
    await this.client.logoutPopup({
      account: this.loggedInMicrosoftUser,
      mainWindowRedirectUri: "/",
    });
    this.clearState();
  }

  async getTokens() {
    const tokenRequest = {
      account: this.loggedInMicrosoftUser ?? this.lastValidUserAccount,
      scopes: [
        "User.Invite.All",
        "Group.Read.All",
        "GroupMember.ReadWrite.All",
        "openid",
        "profile",
        "offline_access",
      ],
      refreshTokenExpirationOffsetSeconds: 7200,
      forceRefresh: this.isFirstTokenRefresh,
    };

    this.isFirstTokenRefresh = false;

    try {
      await this.init();
      if (!this.loggedInMicrosoftUser) {
        console.log("User is not logged in");
        return {};
      }
      const { accessToken, idToken } =
        await this.client.acquireTokenSilent(tokenRequest);
      return { accessToken, idToken };
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log("InteractionRequiredAuthError", error);
        const { accessToken, idToken } =
          await this.client.acquireTokenPopup(tokenRequest);
        return { accessToken, idToken };
      } else {
        console.error("Error acquiring tokens", error);
        return {};
      }
    }
  }

  async getAuthHeader() {
    const { idToken } = await this.getTokens();
    return `Bearer ${idToken}`;
  }

  async getAccessToken() {
    const { accessToken } = await this.getTokens();
    return `${accessToken}`;
  }

  async loadLoggedInUser(): Promise<UserDTO | undefined> {
    const authHeader = await this.getAuthHeader();
    const user = await http
      .auth(authHeader)
      .get("/me")
      .json<UserDTO>()
      .catch(() => undefined);

    if (user) {
      this.state.value.user = user;
      return this.state.value.user;
    } else {
      this.clearState();
      return undefined;
    }
  }

  get loggedInMicrosoftUser(): AccountInfo | undefined {
    const [userAccount] = this.client.getAllAccounts();
    if (userAccount) {
      this.lastValidUserAccount = userAccount;
    }
    return userAccount;
  }

  get loggedInUser(): UserDTO | undefined {
    return this.state.value.user;
  }

  get loggedInUserCustomerNumber(): string | undefined {
    return this.loggedInUser?.customerNumber;
  }

  get authenticated() {
    const microsoftUserRoles =
      this.loggedInMicrosoftUser?.idTokenClaims?.roles ?? [];
    const approvedRoles = [
      Role.ONE_ADMIN,
      Role.CUSTOMER_ADMIN,
      Role.ONE_USER,
      Role.USER,
    ] as string[];
    return microsoftUserRoles.some((role) => approvedRoles.includes(role));
  }
}

export const authService = new AuthService();
